import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Smartphone, ArrowRight } from "lucide-react";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_buildguard-preview/artifacts/3lbt7aob_nirmantra_ai_icon.png";

export default function VendorRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isNew, setIsNew] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (localStorage.getItem("partner_token") && localStorage.getItem("partner_role") === "vendor") navigate("/vendor"); }, [navigate]);

  const requestOtp = async () => {
    if (mobile.trim().length < 10) return toast.error("Enter a valid 10-digit mobile");
    setLoading(true);
    try {
      const { data } = await papi.post("/partner/vendor/otp/request", { mobile });
      setIsNew(data.is_new); setStep("otp");
      toast.success("OTP sent to your mobile");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (isNew && (!name || !email)) return toast.error("Name and email are required");
    setLoading(true);
    try {
      const { data } = await papi.post("/partner/vendor/otp/verify", { mobile, code, name, email });
      localStorage.setItem("partner_token", data.token);
      localStorage.setItem("partner_role", "vendor");
      toast.success("Welcome to Nirmantra Partners!");
      navigate("/vendor");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF9] flex flex-col items-center justify-center px-6 py-10">
      <Link to="/" className="flex items-center gap-2.5 mb-8">
        <img src={LOGO} alt="" className="h-10 w-10 rounded-xl" />
        <span className="font-display font-extrabold text-xl text-slate-900">Nirmantra<span className="text-[#0E9AC8]">.ai</span> <span className="text-slate-400 font-medium text-base">Partners</span></span>
      </Link>
      <div className="card-premium rounded-2xl p-8 w-full max-w-md">
        <div className="h-12 w-12 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center mb-5">
          {step === "phone" ? <Smartphone className="h-6 w-6 text-[#0E9AC8]" /> : <ShieldCheck className="h-6 w-6 text-[#0E9AC8]" />}
        </div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900">
          {step === "phone" ? "Join as a Verified Partner" : "Verify your mobile"}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {step === "phone" ? "Register in under a minute. Only mobile, name & email needed." : `Enter the 6-digit code sent to ${mobile}.`}
        </p>

        {step === "phone" ? (
          <div className="mt-6 space-y-4">
            <div>
              <Label>Mobile number *</Label>
              <Input data-testid="vr-mobile" className="rounded-xl mt-1.5" placeholder="9876543210" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} />
            </div>
            <div><Label>Full name *</Label><Input data-testid="vr-name" className="rounded-xl mt-1.5" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><Label>Email *</Label><Input data-testid="vr-email" type="email" className="rounded-xl mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <Button data-testid="vr-request-otp" onClick={requestOtp} disabled={loading} className="w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send OTP <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {null}
            <div><Label>OTP code *</Label><Input data-testid="vr-otp" className="rounded-xl mt-1.5 tracking-[0.3em] text-center text-lg" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} /></div>
            <Button data-testid="vr-verify" onClick={verify} disabled={loading} className="w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
            </Button>
            <button onClick={() => setStep("phone")} className="w-full text-sm text-slate-500 hover:text-[#0E9AC8]">← Change number</button>
          </div>
        )}
      </div>
      <p className="mt-6 text-sm text-slate-500">Are you a manager/admin? <Link to="/login" className="text-[#0E9AC8] font-semibold">Staff login</Link></p>
    </div>
  );
}
